<script lang="ts">
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {RecordSearchParams} from 'typings/portaro.be.types';
    import type {LogbookReportPairingType} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/types';
    import type {SearchManager} from 'src/features/search/search-manager/search-manager';
    import {getSutorLogbookPairingContext} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/sutor-logbook-pairing-context';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {Kind, SearchType} from 'shared/constants/portaro.constants';
    import {getDateFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {hasRecordReference} from 'src/features/record-grid/lib/types-utils';
    import {fade} from 'svelte/transition';
    import {FOND_PROJECT_BUSINESS_ITEM, FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import {logbookReportsPageSize} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/types';
    import KpBarebonesTable from 'shared/ui-widgets/table/barebones/KpBarebonesTable.svelte';
    import KpPageableSearchResults from 'src/features/search/kp-search-results/KpPageableSearchResults.svelte';
    import KpSearchContext from 'src/features/search/kp-search-context/KpSearchContext.svelte';
    import KpSearchToolbar from 'src/features/search/kp-search-toolbar/KpSearchToolbar.svelte';
    import LogbookReportItem from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/LogbookReportItem.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';

    export let logbookRecord: RecordRow;
    export let pairingType: LogbookReportPairingType;

    export let getSearchManager: () => SearchManager<RecordRow>; // Bind outside

    const context = getSutorLogbookPairingContext();

    const logbookDateField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_PROJECT_BUSINESS_ITEM.INSTALLATION_ITEM.dateFieldId);
    const logbookCustomerProjectField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_PROJECT_BUSINESS_ITEM.INSTALLATION_ITEM.customerProjectFieldId);
    const logbookDate = getDateFromGridFieldValue(logbookDateField);
    const processedReports$ = context.processedReports$;

    function createStaticParams(): RecordSearchParams {
        return {
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_TABLE,
            rootFond: [{id: FOND_REPORT.fond}],
            fond: FOND_REPORT.subfondsForLogbookPairing.map((fondId) => ({id: fondId})),
            pageSize: logbookReportsPageSize,
            qt: JSON.stringify(pairingType === 'unpair' ? createPairedConjunction() : createUnpairedConjunction())
        };
    }

    function createPairedConjunction() {
        return {
            and: [
                {
                    field: {
                        id: FOND_REPORT.parentLinkSearchField,
                        text: FOND_REPORT.parentLinkSearchField
                    },
                    eq: {
                        value: logbookRecord.id
                    }
                }
            ]
        };
    }

    function createUnpairedConjunction() {
        let customerProjectId = context.projectRecord.id;

        if (hasRecordReference(logbookCustomerProjectField)) {
            customerProjectId = logbookCustomerProjectField.recordReference.id;
        }

        return {
            and: [
                {
                    field: {
                        id: FOND_REPORT.dateValSearchField,
                        text: FOND_REPORT.dateValSearchField
                    },
                    eq: {
                        value: logbookDate.toISODate({format: 'basic'})
                    }
                },
                {
                    field: {
                        id: FOND_REPORT.parentLinkSearchField,
                        text: FOND_REPORT.parentLinkSearchField
                    },
                    eq: {
                        value: context.projectRecord.id
                    }
                },
                {
                    field: {
                        id: FOND_REPORT.customerProjectLinkSearchField,
                        text: FOND_REPORT.customerProjectLinkSearchField
                    },
                    eq: {
                        value: customerProjectId
                    }
                }
            ]
        };
    }
</script>

<Flex direction="column" gap="xxl" fillAvailableSpace>
    <KpSearchContext staticParams="{createStaticParams()}" localSearch let:searchManager bind:getSearchManager>
        <KpSearchToolbar/>

        <KpPageableSearchResults pagination="{searchManager.getPagination()}" let:paginationData>
            <KpBarebonesTable fontSize="12px">
                <tr slot="header">
                    {#if pairingType === 'pair'}
                        <th class="button-header"></th>
                    {/if}

                    <th>Pracovník/Stroj</th>
                    <th>Datum</th>
                    <th>Vykázáno</th>
                    <th>Název</th>
                    <th>Typ</th>

                    {#if pairingType === 'unpair'}
                        <th class="button-header"></th>
                    {/if}
                </tr>

                <svelte:fragment slot="body">
                    {#each paginationData.items as record(record.id)}
                        <tr transition:fade={{duration: 250}}>
                            <LogbookReportItem {record} {pairingType} isProcessing="{$processedReports$.includes(record.id)}" on:pair-unpair/>
                        </tr>
                    {/each}
                </svelte:fragment>
            </KpBarebonesTable>
        </KpPageableSearchResults>
    </KpSearchContext>
</Flex>

<style lang="less">
    .button-header {
        width: 70px;
    }
</style>